import React, { useEffect, useState, useContext } from 'react';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { fixToTwoDecimal, formatNumbersToCurrencyString, validateNumericInput } from 'utils/utils';
import { billableExpenseBody } from './helpers/expensesDataTableHelpersV2.js';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import { getUserHourRate } from 'services/timerServices';

const ExpensesV2 = (props) => {
  const {
    isContigencyFee,
    expenses: initialExpenses,
    setExpenses,
    expenseTypes,
    expenseError,
    setExpenseError,
    isChanged,
    setIsChanged,
    addExpense,
    teamMembers,
    deleteExpense,
    expenseTotal,
    caseDetails,
    createExpenses,
    updateExpenses,
    setAllExpensesSaved,
    createExpenseInvoiceClicked,
    setExpenseTotal,
  } = props || {};

  const userContext = useContext(UserDetailsContext);
  const [modifiedRows, setModifiedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const disableExpense = isContigencyFee && !caseDetails?.billing_preferences?.is_expense;

  const markRowAsModified = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev, [rowIndex]: true };
      setAllExpensesSaved(false);
      return updated;
    });

    setIsChanged((prev) => ({ ...prev, time_entries: true }));
  };

  // Mark new entries as modified when they are added so save icon shows immediately
  useEffect(() => {
    if (initialExpenses) {
      initialExpenses.forEach((entry, index) => {
        if ((entry?.new_expense_added || entry?.is_added_entry) && !modifiedRows[index]) {
          setModifiedRows((prev) => {
            if (prev[index]) return prev; // Already marked
            const updated = { ...prev, [index]: true };
            setAllExpensesSaved(false);
            return updated;
          });
          setIsChanged((prev) => ({ ...prev, time_entries: true }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialExpenses]);

  const markRowAsUnmodified = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];

      const hasOtherChanges = Object.keys(updated).length > 0;

      setAllExpensesSaved(!hasOtherChanges);
      setIsChanged((prev) => ({ ...prev, time_entries: hasOtherChanges }));

      return updated;
    });
  };
  const handleRowDeletion = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      setAllExpensesSaved(Object.keys(updated).length === 0);
      return updated;
    });

    setEditingRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setIsChanged((prev) => ({
      ...prev,
      time_entries: Object.keys(modifiedRows).filter((key) => key !== rowIndex.toString()).length > 0,
    }));
  };
  const handleSaveRow = async (rowIndex) => {
    const rowData = editingRows[rowIndex] || initialExpenses[rowIndex];
    const errors = {};
    const feeType = rowData?.fee_type;

    if (!rowData.expense_date) {
      errors[`${rowIndex}_date`] = 'Required';
    }
    if (!rowData.team_member_id) {
      errors[`${rowIndex}_team_member`] = 'Required';
    }
    if (!rowData.expense_type_id) {
      errors[`${rowIndex}_expense_type`] = 'Required';
    }

    if (feeType === 'hourly') {
      if (!rowData.rate_per_unit || parseFloat(rowData.rate_per_unit) <= 0) {
        errors[`${rowIndex}_rate_per_unit`] = 'Required';
      }
      if (!rowData.quantity || parseFloat(rowData.quantity) <= 0) {
        errors[`${rowIndex}_quantity`] = 'Required';
      }
      // Always compute total on save for Hourly
      const total = (Number(rowData.rate_per_unit) || 0) * (Number(rowData.quantity) || 0);
      rowData.total_amount = fixToTwoDecimal(total);
      // clear any fixed-only error
      if (expenseError?.[`${rowIndex}_total_amount`]) {
        errors[`${rowIndex}_total_amount`] = '';
      }
    } else {
      // Fixed
      const total = parseFloat(rowData.total_amount);
      if (!total || total <= 0) {
        errors[`${rowIndex}_total_amount`] = 'Required';
      }

      rowData.rate_per_unit = '';
      rowData.quantity = '';
      // clear hourly-only errors if they exist
      if (expenseError?.[`${rowIndex}_rate_per_unit`]) errors[`${rowIndex}_rate_per_unit`] = '';
      if (expenseError?.[`${rowIndex}_quantity`]) errors[`${rowIndex}_quantity`] = '';
    }

    if (Object.keys(errors).length > 0) {
      setExpenseError((prev) => ({ ...prev, ...errors }));
      return;
    }

    const onExpenseSuccess = () => {
      delete rowData.new_expense_added;

      const updatedExpenses = [...initialExpenses];
      updatedExpenses[rowIndex] = rowData;
      setExpenses(updatedExpenses);

      const updatedEditingRows = { ...editingRows };
      delete updatedEditingRows[rowIndex];
      setEditingRows(updatedEditingRows);

      markRowAsUnmodified(rowIndex);
    };

    try {
      if (rowData.new_expense_added && !rowData?.pk) {
        await createExpenses(rowData, onExpenseSuccess);
      } else {
        await updateExpenses(rowData, onExpenseSuccess);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getRowData = (rowIndex) => {
    return editingRows[rowIndex] || initialExpenses[rowIndex];
  };

  const onChangeBillable = async (e, index) => {
    const checked = e?.target?.checked;
    const updatedRows = { ...editingRows };
    const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

    rowData.billable = checked;
    updatedRows[index] = rowData;
    setEditingRows(updatedRows);

    const updatedTimeEntries = [...initialExpenses];
    updatedTimeEntries[index] = rowData;
    setExpenses(updatedTimeEntries); // Triggers total recalculation

    // 3. Check if ONLY billable was changed (no other edits)
    const isOnlyBillableChange = !modifiedRows[index] && Object.keys(editingRows[index] || {}).length <= 1;

    isChanged.time_entries = true;
    setIsChanged({ ...isChanged });

    if (isOnlyBillableChange) {
      try {
        delete rowData.added_new_time_entry;

        const updatedEditingRows = { ...editingRows };
        delete updatedEditingRows[index];
        setEditingRows(updatedEditingRows);

        markRowAsUnmodified(index);
        setAllExpensesSaved(true);
      } catch (err) {
        console.error(err);

        rowData.billable = !checked;
        updatedRows[index] = rowData;
        setEditingRows(updatedRows);
        updatedTimeEntries[index] = rowData;
        setExpenses(updatedTimeEntries);
      }
    } else {
      markRowAsModified(index);
    }
  };

  useEffect(() => {
    const combinedTimeEntries = initialExpenses.map((entry, idx) => {
      return editingRows[idx] ? editingRows[idx] : entry;
    });

    let subTotal = 0;

    combinedTimeEntries.forEach((entry) => {
      if (entry.billable) {
        let total_amount = entry.total_amount;
        total_amount = total_amount ? parseFloat(total_amount) : 0;
        subTotal += total_amount;
        subTotal = +parseFloat(subTotal).toFixed(2);
      }
    });

    setExpenseTotal(fixToTwoDecimal(subTotal));
  }, [initialExpenses, editingRows]);

  // Validate all expense rows when create invoice is clicked
  useEffect(() => {
    if (createExpenseInvoiceClicked) {
      const errors = {};

      initialExpenses.forEach((entry, rowIndex) => {
        const rowData = editingRows[rowIndex] || entry;
        const feeType = rowData?.fee_type || 'hourly';

        // Validate required fields for ALL rows
        if (!rowData.expense_date) {
          errors[`${rowIndex}_date`] = 'Required';
        }
        if (!rowData.team_member_id) {
          errors[`${rowIndex}_team_member`] = 'Required';
        }
        if (!rowData.expense_type_id) {
          errors[`${rowIndex}_expense_type`] = 'Required';
        }

        // Validate based on fee type
        if (feeType === 'hourly') {
          if (!rowData.rate_per_unit || parseFloat(rowData.rate_per_unit) <= 0) {
            errors[`${rowIndex}_rate_per_unit`] = 'Required';
          }
          if (!rowData.quantity || parseFloat(rowData.quantity) <= 0) {
            errors[`${rowIndex}_quantity`] = 'Required';
          }
        } else {
          // Fixed fee type
          const total = parseFloat(rowData.total_amount);
          if (!total || total <= 0) {
            errors[`${rowIndex}_total_amount`] = 'Required';
          }
        }
      });

      // Update expense errors if any validation errors found
      if (Object.keys(errors).length > 0) {
        setExpenseError((prev) => ({ ...prev, ...errors }));
      }
    }
  }, [createExpenseInvoiceClicked, initialExpenses, editingRows, setExpenseError]);

  // Add Fee Type options
  const feeTypeOptions = [
    { label: 'Hourly', value: 'hourly' },
    { label: 'Fixed', value: 'fixed' },
  ];

  const getFeeType = (row) => row?.fee_type || 'hourly';

  const getFirmUserIdForRate = (selectedMember, teamMemberId) => {
    if (selectedMember?.firm_user_id) return selectedMember.firm_user_id;
    if (!teamMemberId || !caseDetails?.team_member?.length) return null;
    const caseMember = caseDetails.team_member.find(
      (m) => m.contact_id === teamMemberId || m.user_id === teamMemberId || m.client_cognito_username === teamMemberId
    );
    return caseMember?.firm_user_id ?? null;
  };

  const fetchAndSetHourlyRate = async (rowData, firmUserId) => {
    if (!firmUserId || !caseDetails?.case_id) return false;

    try {
      const response = await getUserHourRate(caseDetails.case_id, firmUserId);
      const rate = response?.data?.rate;

      if (rate && typeof Number(rate) === 'number') {
        rowData.rate_per_unit = rate;
        // Auto-calculate total if quantity exists
        if (rowData.quantity) {
          rowData.total_amount = fixToTwoDecimal(Number(rate) * Number(rowData.quantity));
        }
        return true;
      }
    } catch (error) {
      console.error('Error fetching hourly rate:', error);
    }
    return false;
  };

  return (
    <div className="mt-4">
      <div className="caption-bold mb-3">Expenses</div>
      <div className="card-bg">
        <div className="row">
          <div className="col-12 ">
            <div className="datatable-responsive">
              <DataTable className="p-datatable-responsive" value={initialExpenses} emptyMessage="No records found">
                <Column
                  header="Date"
                  field="expense_date"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    const formattedDate = rowEntry.expense_date
                      ? convertDateFromApiToUserTimezone(
                          rowEntry.expense_date,
                          userContext?.userDetails?.timezone,
                          constants.month_date_year_format
                        )
                      : null;
                    return (
                      <>
                        <span className="p-column-title text-break">Date</span>
                        <span className="text-break">{formattedDate || <i>Please Enter</i>}</span>
                        <br></br>
                        {expenseError && expenseError[`${props.rowIndex}_date`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_date`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <div className="align-items-center w-100 d-flex">
                        <span className="p-column-title text-break">Date</span>
                        <div className="table-input">
                          <TimezoneDatePicker
                            value={rowData?.expense_date}
                            onChange={(value) => {
                              let index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              rowData.expense_date = value;
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);

                              expenseError[`${props.rowIndex}_date`] = '';
                              setExpenseError({ ...expenseError });
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                              markRowAsModified(index);
                            }}
                            timezone={userContext?.userDetails?.timezone}
                            isEndOfDay={true}
                          />
                          {expenseError && expenseError[`${props.rowIndex}_date`] && (
                            <span className="text-danger">{expenseError[`${props.rowIndex}_date`]}</span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <Column
                  header="Members"
                  field="team_member_name"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Members</span>
                        <span className="text-break">{rowEntry.team_member_name ? rowEntry.team_member_name : <i>Please Enter</i>}</span>
                        <br></br>
                        {expenseError && expenseError[`${props.rowIndex}_team_member`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_team_member`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    // Find the matching team member (match by contact_id, firm_user_id, or user_id)
                    const selectedTeamMember = teamMembers.find(
                      (tm) =>
                        tm.contact_id === rowData?.team_member_id ||
                        tm.firm_user_id === rowData?.team_member_id ||
                        tm.user_id === rowData?.team_member_id
                    );
                    return (
                      <div className="align-items-center w-100 d-flex">
                        <span className="p-column-title text-break">Members</span>
                        <div className="w-100">
                          <Dropdown
                            options={teamMembers}
                            optionLabel="name"
                            value={selectedTeamMember || null}
                            placeholder="Select"
                            onChange={async (e) => {
                              const index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              const selectedMember = e.target.value || e.value;
                              rowData.team_member_name = selectedMember?.name;
                              rowData.team_member_id = selectedMember?.contact_id;

                              // Auto-fetch hourly rate if fee type is hourly
                              const currentFeeType = rowData?.fee_type || 'hourly';
                              if (currentFeeType === 'hourly') {
                                const firmUserId = getFirmUserIdForRate(selectedMember, rowData.team_member_id);
                                await fetchAndSetHourlyRate(rowData, firmUserId);
                              }

                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);

                              expenseError[`${index}_team_member`] = '';
                              setExpenseError({ ...expenseError });
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                              markRowAsModified(index);
                            }}
                          />
                          {expenseError && expenseError[`${props.rowIndex}_team_member`] && (
                            <span className="text-danger">{expenseError[`${props.rowIndex}_team_member`]}</span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <Column
                  header="Expense Type"
                  field="expense_type"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Expense Type</span>
                        <span className="text-break">{rowEntry.expense_type ? rowEntry.expense_type : <i>Please Enter</i>}</span>
                        <br></br>
                        {expenseError && expenseError[`${props.rowIndex}_expense_type`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_expense_type`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <div className="align-items-center w-100 d-flex">
                        <span className="p-column-title text-break">Expense Type</span>
                        <div className="w-100">
                          <Dropdown
                            value={{
                              expense_type: rowData?.expense_type,
                              expense_type_id: rowData?.expense_type_id,
                            }}
                            options={expenseTypes}
                            onChange={(e) => {
                              let index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              rowData.expense_type = e.target?.value?.expense_type;
                              rowData.expense_type_id = e.target?.value?.expense_type_id;
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);

                              expenseError[`${props.rowIndex}_expense_type`] = '';
                              setExpenseError({ ...expenseError });
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                              markRowAsModified(index);
                            }}
                            optionLabel="expense_type"
                            filter
                            placeholder="Select"
                          />
                          {expenseError && expenseError[`${props.rowIndex}_expense_type`] && (
                            <span className="text-danger">{expenseError[`${props.rowIndex}_expense_type`]}</span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <Column
                  header="Description"
                  field="description"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Description</span>
                        <span className="text-break">{rowEntry.description ? rowEntry.description : <i>Please Enter</i>}</span>
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <div className="align-items-center w-100 d-flex">
                        {' '}
                        <span className="p-column-title text-break">Description</span>
                        <TextSnippetWrapper
                          type="text"
                          value={rowData?.description}
                          onChange={(e) => {
                            let index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            rowData.description = e.target.value;
                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);

                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          className="w-100 input-shadow"
                          autoComplete="off"
                        />
                      </div>
                    );
                  }}
                />
                <Column
                  header="Fee Type"
                  field="fee_type"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Fee Type</span>
                        <span className="text-break text-capitalize">{rowEntry.fee_type || 'Hourly'}</span>
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <div className="align-items-center w-100 d-flex">
                        {' '}
                        <span className="p-column-title text-break">Fee Type</span>
                        <Dropdown
                          value={rowData?.fee_type || 'hourly'}
                          options={feeTypeOptions}
                          onChange={async (e) => {
                            const index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            rowData.fee_type = e.value;

                            if (e.value === 'fixed') {
                              // Switch to fixed: clear hourly fields
                              rowData.rate_per_unit = null;
                              rowData.quantity = null;
                              rowData.total_amount = '';
                              expenseError[`${index}_rate_per_unit`] = '';
                              expenseError[`${index}_quantity`] = '';
                            } else {
                              // Switch to hourly: fetch rate if team member is selected
                              const selectedMember = teamMembers.find(
                                (tm) =>
                                  tm.contact_id === rowData.team_member_id ||
                                  tm.firm_user_id === rowData.team_member_id ||
                                  tm.user_id === rowData.team_member_id
                              );

                              const firmUserId = getFirmUserIdForRate(selectedMember, rowData.team_member_id);
                              const rateFetched = await fetchAndSetHourlyRate(rowData, firmUserId);

                              // If rate not fetched, preserve existing or set empty
                              if (!rateFetched) {
                                rowData.rate_per_unit = rowData.rate_per_unit || '';
                              }

                              rowData.quantity = rowData.quantity || '';
                              rowData.total_amount = rowData.rate_per_unit * rowData.quantity || 0;
                              expenseError[`${index}_total_amount`] = '';
                            }

                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);
                            setExpenseError({ ...expenseError });
                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          placeholder="Select"
                        />
                      </div>
                    );
                  }}
                />

                {/* Fee (hourly only) */}
                <Column
                  header="Fee"
                  field="rate_per_unit"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    const feeType = getFeeType(rowEntry);
                    return (
                      <>
                        <span className="p-column-title text-break">Fee</span>
                        {feeType === 'fixed' ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <>
                            <span className="text-break">
                              {rowEntry.rate_per_unit ? formatNumbersToCurrencyString(rowEntry.rate_per_unit) : <i>Please Enter</i>}
                            </span>
                            {/* <br></br> */}
                            <br />
                            {expenseError && expenseError[`${props.rowIndex}_rate_per_unit`] && (
                              <span className="text-danger">{expenseError[`${props.rowIndex}_rate_per_unit`]}</span>
                            )}
                          </>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    if (getFeeType(rowData) === 'fixed') {
                      return (
                        <>
                          <span className="p-column-title text-break">Fee</span>
                          <span className="text-muted ms-2">-</span>
                        </>
                      );
                    }
                    return (
                      <div className="align-items-center w-100 d-flex">
                        <span className="p-column-title text-break">Fee</span>
                        <div>
                          <InputText
                            value={rowData?.rate_per_unit || ''}
                            onFocus={(e) => {
                              // Clear the field if it's 0 when user focuses
                              if (e.target.value === '0' || parseFloat(e.target.value) === 0) {
                                e.target.value = '';
                              }
                            }}
                            onChange={(e) => {
                              const index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              // Validate input to allow only numbers and decimals
                              const validatedValue = validateNumericInput(e.target.value);

                              let quantity = rowData.quantity;
                              let rate = validatedValue;
                              quantity = quantity ? parseFloat(quantity) : 0;
                              rate = rate ? parseFloat(rate) : 0;
                              let total = quantity * rate;

                              rowData.rate_per_unit = validatedValue === '' ? '' : validatedValue;
                              rowData.total_amount = fixToTwoDecimal(total);
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);

                              expenseError[`${props.rowIndex}_rate_per_unit`] = '';
                              setExpenseError({ ...expenseError });
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                              markRowAsModified(index);
                            }}
                            className="w-100 input-shadow"
                            type="text"
                            maxLength={13}
                            placeholder="0.00"
                          />
                          {expenseError && expenseError[`${props.rowIndex}_rate_per_unit`] && (
                            <span className="text-danger">{expenseError[`${props.rowIndex}_rate_per_unit`]}</span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                {/* Quantity (hourly only) */}
                <Column
                  header="Quantity"
                  field="quantity"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    const feeType = getFeeType(rowEntry);
                    return (
                      <>
                        <span className="p-column-title text-break">Quantity</span>
                        {feeType === 'fixed' ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <>
                            <span className="text-break">{rowEntry.quantity ? rowEntry.quantity : <i>Please Enter</i>}</span>
                            {/* <br></br> */}
                            <br />
                            {expenseError && expenseError[`${props.rowIndex}_quantity`] && (
                              <span className="text-danger">{expenseError[`${props.rowIndex}_quantity`]}</span>
                            )}
                          </>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    if (getFeeType(rowData) === 'fixed') return <span className="text-muted">-</span>;
                    return (
                      <div className="align-items-center w-100 d-flex">
                        <span className="p-column-title text-break">Quantity</span>
                        <div>
                          <InputText
                            value={rowData?.quantity || ''}
                            onFocus={(e) => {
                              // Clear the field if it's 0 when user focuses
                              if (e.target.value === '0' || parseFloat(e.target.value) === 0) {
                                e.target.value = '';
                              }
                            }}
                            onChange={(e) => {
                              const index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              // Validate input to allow only numbers and decimals
                              const validatedValue = validateNumericInput(e.target.value);

                              let quantity = validatedValue;
                              let rate = rowData.rate_per_unit;
                              quantity = quantity ? parseFloat(quantity) : 0;
                              rate = rate ? parseFloat(rate) : 0;
                              let total = quantity * rate;

                              rowData.quantity = validatedValue === '' ? '' : validatedValue;
                              rowData.total_amount = fixToTwoDecimal(total);
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);

                              expenseError[`${props.rowIndex}_quantity`] = '';
                              setExpenseError({ ...expenseError });
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                              markRowAsModified(index);
                            }}
                            className="w-100 input-shadow"
                            type="text"
                            maxLength={13}
                            placeholder="0"
                          />
                          {expenseError && expenseError[`${props.rowIndex}_quantity`] && (
                            <span className="text-danger">{expenseError[`${props.rowIndex}_quantity`]}</span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <Column
                  header="Total"
                  field="total_amount"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Total</span>
                        <span className="text-break">{formatNumbersToCurrencyString(rowEntry?.total_amount)}</span>
                        <br />
                        {expenseError && expenseError[`${props.rowIndex}_total_amount`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_total_amount`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    if (rowData?.fee_type === 'fixed') {
                      return (
                        <div className="align-items-center w-100 d-flex">
                          <span className="p-column-title text-break">Total</span>
                          <InputText
                            value={rowData?.total_amount || ''}
                            onFocus={(e) => {
                              // Clear the field if it's 0 when user focuses
                              if (e.target.value === '0' || parseFloat(e.target.value) === 0) {
                                e.target.value = '';
                              }
                            }}
                            onChange={(e) => {
                              const index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              // Validate input to allow only numbers and decimals
                              const validatedValue = validateNumericInput(e.target.value);

                              rowData.total_amount = validatedValue === '' ? '' : validatedValue;
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);
                              expenseError[`${props.rowIndex}_total_amount`] = '';
                              setExpenseError({ ...expenseError });
                              markRowAsModified(index);
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                            }}
                            className="w-100 input-shadow"
                            type="text"
                            maxLength={13}
                            placeholder="0.00"
                          />
                        </div>
                      );
                    }
                    return <span>{formatNumbersToCurrencyString(rowData?.total_amount)}</span>;
                  }}
                />
                <Column
                  header="Billable"
                  field="billable"
                  body={(rowData, props) => {
                    return billableExpenseBody({
                      rowData: getRowData(props.rowIndex),
                      props,
                      onChangeBillable,
                      disableExpense,
                    });
                  }}
                />
                <Column
                  body={(rowData, props) => {
                    const rowIndex = props?.rowIndex;
                    const rowEntry = getRowData(rowIndex);
                    const isNewEntry = rowEntry?.new_expense_added || rowEntry?.is_added_entry;
                    const hasModifications = modifiedRows[rowIndex];
                    const showSaveIcon = hasModifications || isNewEntry;

                    return (
                      <>
                        <div className="d-flex w-100 align-items-start">
                          <span className="p-column-title text-break">Actions</span>

                          {/* Save and Delete in one row */}
                          <div className="d-flex align-items-center gap-2">
                            {showSaveIcon && (
                              <>
                                <i
                                  className={`bi bi-check2-circle cursor-pointer save-icon-row-${props?.rowIndex}`}
                                  style={{
                                    marginRight: '10px',
                                    fontSize: '13px',
                                    marginTop: '1px',
                                  }}
                                  onClick={() => handleSaveRow(props?.rowIndex)}
                                ></i>
                                <Tooltip
                                  target={`.save-icon-row-${rowIndex}`}
                                  content="Save to add amount to total"
                                  position="top"
                                  showDelay={500}
                                />
                              </>
                            )}

                            <i
                              className="icon-delete icon-red cursor-pointer"
                              onClick={() => {
                                deleteExpense(props);
                                handleRowDeletion(rowIndex);
                              }}
                            />
                          </div>
                        </div>

                        {createExpenseInvoiceClicked && modifiedRows[rowIndex] && <div className="text-danger small mt-1">Please Save</div>}
                      </>
                    );
                  }}
                />
              </DataTable>
              <div>
                <div className="d-flex mt-2 p-2">
                  <div className="col col-md-8 text-bold">Total:</div>
                  <div className="col col-md-4 text-bold">{formatNumbersToCurrencyString(expenseTotal)}</div>
                </div>
                <div className="col-12 p-2">
                  <span className="mt-3 addfield addfield-inline" role="button" onClick={addExpense}>
                    + Add Expense
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesV2;
